"use client";

import { useState } from "react";
import Link from "next/link";

import { If } from "~/components/v3/Conditional";
import { Button } from "~/components/v3/themed/Button";
import { EventImage } from "~/components/v3/events-components/EventImage";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "~/components/v4/shadcn/Tabs";

import { ProfilePageData } from "~/app/profile/[profileSlug]/actions";
import { PastEventsList } from "./events/PastEventsList";
import { UpcomingEventsList } from "./events/UpcomingEventsList";

export const UserEventList = ({
    user,
    isCurrentUser,
    showReimbursementButton,
}: {
    user: {
        id: string;
        name: string;
        image: string;
    };
    isCurrentUser: boolean;
    showReimbursementButton?: boolean;
}) => {
    const [selectedTab, setSelectedTab] = useState<
        "all" | "hosting" | "attending" | "draft"
    >("all");

    return (
        <div className="flex w-full flex-col justify-between gap-2 pb-6">
            <Tabs
                value={selectedTab}
                onValueChange={(value) =>
                    setSelectedTab(value as typeof selectedTab)
                }
            >
                <TabsList className="w-full">
                    <TabsTrigger value="all">All</TabsTrigger>
                    <TabsTrigger value="hosting">Hosting</TabsTrigger>
                    <TabsTrigger value="attending">Attending</TabsTrigger>
                    {isCurrentUser && (
                        <TabsTrigger value="draft">Draft</TabsTrigger>
                    )}
                </TabsList>

                <TabsContent value="all">
                    <NestedTabs user={user} filterBy="all" />
                </TabsContent>

                <TabsContent value="hosting">
                    <NestedTabs user={user} filterBy="hosting" />
                </TabsContent>

                <TabsContent value="attending">
                    <NestedTabs user={user} filterBy="attending" />
                </TabsContent>

                <If condition={isCurrentUser}>
                    <TabsContent value="draft">
                        <NestedTabs user={user} filterBy="draft" />
                    </TabsContent>
                </If>
            </Tabs>

            <If condition={isCurrentUser && !!showReimbursementButton}>
                <Link href={`/reimbursement-requests/list`} target="_blank">
                    <Button variant="tertiary" className="font-normal">
                        Reimbursement requests
                    </Button>
                </Link>
            </If>
        </div>
    );
};

export const NestedTabs = ({
    user,
    filterBy,
}: {
    user: {
        id: string;
        name: string;
        image: string;
    };
    filterBy: "all" | "hosting" | "attending" | "draft";
}) => {
    return (
        <Tabs defaultValue="upcoming" className="w-full md:mb-0">
            <TabsList className="w-max gap-1 rounded-lg border-none bg-v3-neutral  p-1">
                <TabsTrigger
                    value="upcoming"
                    className="rounded-lg border-none bg-transparent px-2 py-0 text-sm font-[500] text-v3-light-gray shadow-none data-[state=active]:bg-white data-[state=active]:text-black"
                >
                    Upcoming
                </TabsTrigger>
                <TabsTrigger
                    value="past"
                    className="rounded-lg border-none bg-transparent px-2 py-0 text-sm font-[500] text-v3-light-gray shadow-none data-[state=active]:bg-white data-[state=active]:text-black"
                >
                    Past
                </TabsTrigger>
            </TabsList>

            <TabsContent value="upcoming">
                <UpcomingEventsList user={user} filterBy={filterBy} />
            </TabsContent>
            <TabsContent value="past">
                <PastEventsList user={user} filterBy={filterBy} />
            </TabsContent>
        </Tabs>
    );
};

// TODO: this will be a carousel
export const FutureHostingEventsList = ({
    futureHostingEvents,
}: {
    futureHostingEvents: ProfilePageData["futureHostingEvents"];
}) => {
    return (
        <div className="relative mt-8 flex flex-col gap-2" id="hosting-reviews">
            <h2 className="mb-4 text-base font-[400] text-v3-N300">Host</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {futureHostingEvents.map((e) => (
                    <div key={e.slug} className="flex w-full flex-col gap-3">
                        <Link
                            href={`/events/${e.slug}`}
                            target="_blank"
                            className="relative w-full rounded-2xl"
                        >
                            <EventImage
                                event={e}
                                options={{
                                    type: "contain",
                                }}
                                isReadonly
                                allowAnimations
                            />
                        </Link>

                        {e.community ? (
                            <Link
                                href={`/${e.community?.slug}`}
                                className="leading-none text-v3-N300"
                                target="_blank"
                            >
                                {e.community?.name}
                            </Link>
                        ) : (
                            <Link
                                href={`/${e.user?.slug}`}
                                className="leading-none text-v3-N300"
                                target="_blank"
                            >
                                {e.user?.name}
                            </Link>
                        )}

                        <Link href={`/events/${e.slug}`} target="_blank">
                            <h3 className="text-lg leading-none">{e.name}</h3>
                        </Link>
                    </div>
                ))}
            </div>
        </div>
    );
};
